import {
    Box,
    Menu,
    MenuItem,
    IconButton,
    Tooltip,
    TextField,
    Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import useAxiosRequests from "../../../hooks/useAxiosRequest";
import ExternalRoutes from "../../../config/ExternalRoutes";
import ShowUser from "./ShowUser";
import UserEdit from "./UserEdit";

const RenderCellDocument = ({ row }) => {
    const [documents, setDocuments] = useState(null);
    const navigate = useNavigate();
    const { sendRequest } = useAxiosRequests();
    const handleView = () => {
        if (documents > 0) {
            navigate('/contab-admin/document', { state: { firstUser: row } });
        }
    };

    const fetchDataDoc = useCallback(async () => {
        try {
            const response = await sendRequest({
                method: "GET",
                url: ExternalRoutes.document.list_by_user + row.id,
            });
            setDocuments(response?.totalItems ?? 0);
        } catch (error) {
            console.error(error.message);
            setDocuments(0);
        }
    }, [row.id, sendRequest]);

    useEffect(() => {
        fetchDataDoc();
    }, []);

    return (
        <Tooltip title="Voir les documents">
            <span
                className="kl-link-admin"
                style={{ cursor: "pointer", color: documents > 0 ? "#1976d2" : "inherit" }}
                onClick={handleView}
            >
                {documents ?? 0}
            </span>
        </Tooltip>
    );
};

const RenderCellSociete = ({ row }) => {
    const society = row.conSocieties || {};
    return (
        <Tooltip title={society.sctName || ""}>
            <span className="kl-link-admin">{society.sctName || ""}</span>
        </Tooltip>
    );
};

const RowActionsMenu = ({ rowData, onShowClick, onEditClick }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton size="small" onClick={handleOpenMenu}>
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ style: { minWidth: "8rem", zIndex: 1300 } }}
            >
                <MenuItem onClick={() => { onShowClick(rowData); handleCloseMenu(); }}>Voir</MenuItem>
                <MenuItem onClick={() => { onEditClick(rowData); handleCloseMenu(); }}>Modifier</MenuItem>
            </Menu>
        </>
    );
};

const columns = (handleShowClick, handleEditClick) => [
    {
        field: "email",
        headerName: "Email",
        flex: 0.2,
        minWidth: 150,
        renderCell: (params) => (
            <Tooltip title={params.value}>
                <span>{params.value}</span>
            </Tooltip>
        ),
    },
    {
        field: "usrLastname",
        headerName: "Nom",
        flex: 0.15,
        minWidth: 120,
        renderCell: (params) => (
            <Tooltip title={params.value}>
                <span>{params.value}</span>
            </Tooltip>
        ),
    },
    {
        field: "usrFirstname",
        headerName: "Prénom",
        flex: 0.15,
        minWidth: 120,
        renderCell: (params) => (
            <Tooltip title={params.value}>
                <span>{params.value}</span>
            </Tooltip>
        ),
    },
    {
        field: "usrTel",
        headerName: "Téléphone",
        flex: 0.15,
        minWidth: 120,
        renderCell: (params) => (
            <Tooltip title={params.value}>
                <span>{params.value}</span>
            </Tooltip>
        ),
    },
    {
        field: "conSocieties",
        headerName: "Société",
        flex: 0.1,
        minWidth: 100,
        renderCell: RenderCellSociete,
    },
    {
        field: "documents",
        headerName: "Documents",
        flex: 0.1,
        minWidth: 100,
        renderCell: RenderCellDocument,
    },
    {
        field: "usrCreatedAt",
        headerName: "Date d'inscription",
        flex: 0.15,
        minWidth: 120,
        renderCell: (params) => {
            const date = params.value
                ? new Date(params.value).toLocaleDateString("fr-FR")
                : "";
            return (
                <Tooltip title={date}>
                    <span>{date}</span>
                </Tooltip>
            );
        },
    },
    {
        field: "actions",
        headerName: "Actions",
        flex: 0.1,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => (
            <RowActionsMenu
                rowData={params.row}
                onShowClick={handleShowClick}
                onEditClick={handleEditClick}
            />
        ),
    },
];

const UserList = () => {
    const { state } = useLocation();
    const { firstUser } = state || {};
    const { sendRequest } = useAxiosRequests();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalUsers, setTotalUsers] = useState(0);
    const [sortModel, setSortModel] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [searchQuery, setSearchQuery] = useState("");
    const [openUpdateDialog, setOpenUpdateDialog] = useState(!!firstUser);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedUserData, setSelectedUserData] = useState(firstUser || null);

    const fetchUsers = useCallback(async () => {
        try {
            const params = {
                page: paginationModel.page + 1,
                itemsPerPage: paginationModel.pageSize,
                query: searchQuery,
            };

            if (sortModel.length > 0) {
                const { field, sort } = sortModel[0];
                params.order = { [field]: sort };
            }

            const response = await sendRequest({
                method: "GET",
                url: ExternalRoutes.users.list,
                config: { params },
            });

            if (Array.isArray(response?.member)) {
                setUsers(response.member);
                setTotalUsers(response.totalItems);
            } else {
                console.error("Réponse invalide :", response);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des utilisateurs :", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, searchQuery, sortModel, sendRequest]);

    useEffect(() => {
        fetchUsers();
    }, [paginationModel, sortModel]);

    const handlePaginationChange = (model) => {
        setLoading(true);
        setPaginationModel(model);
    };

    const handleSortModelChange = (model) => {
        setLoading(true);
        setSortModel(model);
    };

    const handleOpenUpdateDialog = (data) => {
        setSelectedUserData(data);
        setOpenUpdateDialog(true);
    };

    const handleCloseUpdateDialog = () => {
        setSelectedUserData(null);
        setOpenUpdateDialog(false);
    };

    const handleOpenEditDialog = (data) => {
        setSelectedUserData(data);
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setSelectedUserData(null);
        setOpenEditDialog(false);
    };

    return (
        <>
            <Box className="kl-admin-header" sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Liste des Utilisateurs
                </Typography>
            </Box>

            <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
            />

            <DataGrid
                disableColumnMenu
                rows={users}
                rowCount={totalUsers}
                loading={loading}
                columns={columns(handleOpenUpdateDialog, handleOpenEditDialog)}
                sortingMode="server"
                paginationMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                pageSizeOptions={[10, 25, 50]}
                autoHeight
                disableRowSelectionOnClick
                localeText={{
                    noRowsLabel: "Aucune donnée disponible",
                    MuiTablePagination: {
                        labelRowsPerPage: "Lignes par page",
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`,
                    },
                }}
            />

        <ShowUser
                open={openUpdateDialog}
                handleClose={handleCloseUpdateDialog}
                userData={selectedUserData}
                onUpdated={fetchUsers}
            />
            <UserEdit
                open={openEditDialog}
                handleClose={handleCloseEditDialog}
                userData={selectedUserData}
                onUpdate={fetchUsers}
            />
        </>
    );
};

export default UserList;
